/**
 * Frontend mirror of App\Service\Jha\JhaDocumentTriggerEvaluator (PHP).
 *
 * Загружает триггеры из IDB (JHA_DOCUMENT_TRIGGER_STORE) и проверяет,
 * какие документы должны быть Required на текущей JHA на основе её
 * Work Activities / Energy Sources / Hazards.
 *
 * Логика:
 *   - триггер сработал, если хотя бы один condition matched (OR между conditions)
 *   - condition matched, если все его НЕПУСТЫЕ поля matched (AND внутри condition)
 *   - matching по нормализованным значениям (trim + lowercase) для устойчивости
 */
import { initializeDB } from '../../../config/db';
import { JHA_DOCUMENT_TRIGGER_STORE } from '../../../config/constants';

const SPECIAL_FARP = 'fall_arrest_rescue_plan';

/** «Critical Lift» закрывается поданной Lift Evaluation Form. Узнаём по названию. */
const isCriticalLiftDoc = (name) => /critical\s*lift|lift\s*evaluation/i.test(String(name || ''));

const norm = (v) => typeof v === 'string' ? v.trim().toLowerCase() : '';

/**
 * Возвращает НОВЫЙ stepTwo с добавленными строками для документов из missing[],
 * которых ещё нет в списке (по имени). Каждый добавляется в customDocsPermits
 * с required='Yes Req'.
 *
 * Нужно, когда БЭКЕНД (ответ 422) требует документ, которого НЕТ в локальном
 * списке — например локальный оценщик не сматчил триггер (energy/hazard резолвятся
 * на бэке через связи БД, а на устройстве данные/триггеры могли разойтись или
 * устареть). Без этой инъекции рабочему просто нечем прикрепить файл/форму:
 * документ «required», но строки нет (см. кейс FARP в поле).
 *
 * @param {object} stepTwo  jhaData.stepTwo
 * @param {Array<{document_name?:string,name?:string}>} missing
 * @returns {{ stepTwo: object, added: string[] }}  added — имена добавленных доков
 */
export function injectMissingDocRows(stepTwo, missing) {
    const base = stepTwo?.docsPermits || [];
    const custom = stepTwo?.customDocsPermits || [];
    const existing = new Set([...base, ...custom].map(d => norm(d?.value)));
    const seen = new Set();
    const toAdd = [];
    const added = [];
    for (const m of (missing || [])) {
        const name = (m?.document_name || m?.name || '').trim();
        const key = norm(name);
        if (!name || existing.has(key) || seen.has(key)) continue;
        seen.add(key);
        toAdd.push({ value: name, required: 'Yes Req', attachments: [] });
        added.push(name);
    }
    if (!toAdd.length) return { stepTwo, added: [] };
    return { stepTwo: { ...(stepTwo || {}), customDocsPermits: [...custom, ...toAdd] }, added };
}

/**
 * Имя документа похоже на Fall Arrest Rescue Plan?
 */
export function isFarpDocName(value) {
    const v = norm(value);
    return v === 'fall arrest rescue plan' || v.includes('fall arrest') || v.includes('rescue plan');
}

/**
 * Удовлетворён ли FARP СИНХРОННО по данным stepTwo: на документе Fall Arrest
 * Rescue Plan есть фото/файл-вложение ИЛИ привязка linkedFarpUuid.
 *
 * НЕ учитывает поданную цифровую форму в IDB (это отдельный async-чек
 * checkExisting / hasFarpSubmittedLocal) — компонент комбинирует оба результата.
 * Нужно, чтобы баннер «FARP required» и soft-warning «not found» гасли, когда
 * юзер просто приложил фото бланка (раньше они смотрели только на цифровую форму).
 */
export function isFarpAttachedOrLinked(jhaData) {
    const docs = [
        ...(jhaData?.stepTwo?.docsPermits || []),
        ...(jhaData?.stepTwo?.customDocsPermits || []),
    ];
    return docs.some(d =>
        isFarpDocName(d?.value) && (
            (Array.isArray(d?.attachments) && d.attachments.length > 0) || !!d?.linkedFarpUuid
        )
    );
}

/**
 * Возвращает все активные триггеры из IDB.
 * @returns {Promise<Array>}
 */
export async function loadTriggers() {
    try {
        const db = await initializeDB();
        if (!db.objectStoreNames.contains(JHA_DOCUMENT_TRIGGER_STORE)) {
            return [];
        }
        const all = await db.getAll(JHA_DOCUMENT_TRIGGER_STORE);
        return (all || []).filter(t => t && t.status !== false && Array.isArray(t.conditions) && t.conditions.length > 0);
    } catch (e) {
        console.error('[documentTriggerEvaluator] loadTriggers failed', e);
        return [];
    }
}

/**
 * Извлекает имя хазарда из элемента массива JhaEnergyExposure.hazards
 * (может быть строкой или объектом с name/value/label).
 */
function extractHazardName(h) {
    if (typeof h === 'string') return h;
    if (h && typeof h === 'object') {
        return h.name || h.value || h.label || h.title || null;
    }
    return null;
}

/**
 * Собирает контекст текущей JHA: множества work activities / energy sources / hazards.
 *
 * stepThree.energyActivityRows — массив { workActivity, energySelections: [{energyName, hazards: []}] }
 */
export function buildJhaContext(stepThree, stepOne) {
    const wa = new Set();
    const es = new Set();
    const hz = new Set();

    // Work activities — обычно в stepThree.workActivities (string[])
    const allActivities = [
        ...(stepThree?.workActivities || []),
        ...(stepThree?.customWorkActivities || []),
    ];
    allActivities.forEach(a => {
        if (typeof a === 'string' && a.trim()) wa.add(norm(a));
    });

    // Plus workActivity field on each energyActivityRow (legacy)
    (stepThree?.energyActivityRows || []).forEach(row => {
        if (typeof row?.workActivity === 'string' && row.workActivity.trim()) {
            wa.add(norm(row.workActivity));
        }
        (row?.energySelections || []).forEach(sel => {
            const name = sel?.energyName;
            if (typeof name === 'string' && name.trim()) es.add(norm(name));
            (sel?.hazards || []).forEach(h => {
                const n = extractHazardName(h);
                if (n) hz.add(norm(n));
            });
        });
    });

    // Older shape: stepThree.energyExposures (used in some draft records)
    (stepThree?.energyExposures || []).forEach(exp => {
        if (typeof exp?.workActivity === 'string' && exp.workActivity.trim()) {
            wa.add(norm(exp.workActivity));
        }
        const name = exp?.energyName || exp?.value;
        if (typeof name === 'string' && name.trim()) es.add(norm(name));
        (exp?.hazards || []).forEach(h => {
            const n = extractHazardName(h);
            if (n) hz.add(norm(n));
        });
    });

    return { wa, es, hz };
}

function matchCondition(cond, ctx) {
    if (cond.workActivityValue && !ctx.wa.has(norm(cond.workActivityValue))) return false;
    if (cond.energySourceValue && !ctx.es.has(norm(cond.energySourceValue))) return false;
    if (cond.hazardValue && !ctx.hz.has(norm(cond.hazardValue))) return false;
    // empty condition (all 3 nullable) — never matches
    if (!cond.workActivityValue && !cond.energySourceValue && !cond.hazardValue) return false;
    return true;
}

/**
 * Оценивает все триггеры для данной JHA.
 *
 * @returns {Array<{
 *   triggerId: number,
 *   docsPermitsId: number|null,
 *   specialDocumentType: string|null,
 *   documentName: string,
 *   documentKey: string,
 *   matchedConditionIndex: number,
 *   matchedCondition: object,
 * }>}
 */
export function evaluateTriggers(triggers, stepThree, stepOne) {
    if (!Array.isArray(triggers) || triggers.length === 0) return [];
    const ctx = buildJhaContext(stepThree || {}, stepOne || {});
    const matched = [];

    for (const t of triggers) {
        const conditions = Array.isArray(t.conditions) ? t.conditions : [];
        for (const c of conditions) {
            if (matchCondition(c, ctx)) {
                matched.push({
                    triggerId: t.id,
                    docsPermitsId: t.docsPermitsId ?? null,
                    specialDocumentType: t.specialDocumentType ?? null,
                    documentName: t.documentName || '',
                    documentKey: t.documentKey || '',
                    matchedConditionIndex: c.orderIndex || 1,
                    matchedCondition: c,
                });
                break;
            }
        }
    }
    return matched;
}

/**
 * Высокоуровневый helper: возвращает Map<documentName_lower, matchEntry>
 * для удобного использования в Step 2 (где документы маппятся по имени).
 */
export function buildRequiredMap(matched) {
    const map = new Map();
    matched.forEach(m => {
        if (m.documentName) map.set(norm(m.documentName), m);
    });
    return map;
}

/**
 * Удобное описание сматченного condition для отображения юзеру.
 */
export function describeMatch(matchEntry) {
    if (!matchEntry?.matchedCondition) return '';
    const c = matchEntry.matchedCondition;
    const parts = [];
    if (c.workActivityValue) parts.push(`Work Activity: ${c.workActivityValue}`);
    if (c.energySourceValue) parts.push(`Energy: ${c.energySourceValue}`);
    if (c.hazardValue) parts.push(`Hazard: ${c.hazardValue}`);
    return parts.join(' AND ');
}

/**
 * Считает документ "удовлетворённым", если в jhaData.stepTwo для него:
 *   - есть attachment (фото/файл), ИЛИ
 *   - привязан CSEP (linkedCsepUuid), ИЛИ
 *   - привязан LTT (linkedLttUuid).
 *
 * Для special FARP-типа: достаточно прикреплённого фото в customDocsPermits
 * с именем "Fall Arrest Rescue Plan". Submitted-form-detection живёт в IDB
 * `fallArrestRescuePlans` store — это offline-first check (см. ниже).
 */
function isDocSatisfiedLocal(matchEntry, jhaData) {
    const stepTwo = jhaData?.stepTwo || {};
    const all = [
        ...(stepTwo.docsPermits || []).map(d => ({ ...d, __isCustom: false })),
        ...(stepTwo.customDocsPermits || []).map(d => ({ ...d, __isCustom: true })),
    ];

    const targetName = norm(matchEntry.documentName);

    for (const d of all) {
        if (norm(d.value) !== targetName) continue;
        if (Array.isArray(d.attachments) && d.attachments.length > 0) return true;
        if (d.linkedCsepUuid) return true;
        if (d.linkedLttUuid) return true;
        if (d.linkedFarpUuid) return true;
    }
    return false;
}

/**
 * Доп. проверка для FARP — смотрит submitted FARP в локальной IDB
 * (fallArrestRescuePlans store) для этой JHA. Срабатывает только для
 * специального типа fall_arrest_rescue_plan.
 *
 * Импорт без circular dep — динамический (избегаем циклов между evaluator
 * и fallArrestRescuePlanService).
 */
async function hasFarpSubmittedLocal(jhaData) {
    if (!jhaData?.localId && !(jhaData?.stepOne?.jobNumber && jhaData?.stepOne?.date)) {
        return false;
    }
    try {
        const { default: farpService } = await import('../../../services/fallArrestRescuePlanService');
        // ВАЖНО для нескольких JHA за один день: если у JHA есть стабильный localId —
        // матчим FARP СТРОГО по jhaLocalId, без fallback на job+date. Иначе один FARP
        // (или FARP другой работы за тот же день) ошибочно зачёлся бы как выполненный
        // для всех JHA этого job+date. Fallback на job+date — только для legacy-записей
        // без localId.
        const criteria = jhaData.localId
            ? { jhaLocalId: jhaData.localId }
            : { jobCode: jhaData?.stepOne?.jobNumber, date: jhaData?.stepOne?.date };
        const result = await farpService.checkExisting(criteria);
        return !!result?.exists;
    } catch (e) {
        console.warn('[evaluator] FARP local-check failed', e);
        return false;
    }
}

/**
 * То же для Lift Evaluation Form: смотрим поданные формы в локальной IDB.
 * Матчим строго по jhaLocalId, если он есть, — иначе форма другой работы
 * за тот же день ошибочно зачлась бы как выполненная.
 */
async function hasLiftEvaluationSubmittedLocal(jhaData) {
    if (!jhaData?.localId && !(jhaData?.stepOne?.jobNumber && jhaData?.stepOne?.date)) {
        return false;
    }
    try {
        const { liftEvaluationService } = await import('../../../services/liftEvaluationService');
        const all = await liftEvaluationService.getAllFromIndexedDB();
        return (all || []).some((f) => {
            if (f.status !== 'submitted') return false;
            if (jhaData.localId) return f.jhaLocalId === jhaData.localId;
            return f.jobCode === jhaData?.stepOne?.jobNumber && f.liftDate === jhaData?.stepOne?.date;
        });
    } catch (e) {
        console.warn('[evaluator] Lift Evaluation local-check failed', e);
        return false;
    }
}

/**
 * Полная offline-проверка: возвращает массив missing-документов
 * для блокировки submit ДО отправки POST на бэк.
 *
 * Контракт совпадает с backend findMissingDocuments — те же поля:
 *   { document_name, document_key, reason }
 *
 * @returns {Promise<Array<{document_name: string, document_key: string, reason: string}>>}
 */
export async function findMissingDocumentsLocal(jhaData) {
    const triggers = await loadTriggers();
    if (!triggers.length) return [];

    const matched = evaluateTriggers(triggers, jhaData?.stepThree || {}, jhaData?.stepOne || {});
    if (!matched.length) return [];

    const missing = [];
    const seen = new Set(); // дедуп: один документ — одна строка, даже если сматчили
                            // несколько триггеров/условий на тот же документ.
    for (const m of matched) {
        const dedupKey = m.documentKey || norm(m.documentName);
        if (seen.has(dedupKey)) continue;

        if (isDocSatisfiedLocal(m, jhaData)) { seen.add(dedupKey); continue; }

        // FARP — дополнительная проверка по локально-поданным формам
        if (m.specialDocumentType === SPECIAL_FARP) {
            const ok = await hasFarpSubmittedLocal(jhaData);
            if (ok) { seen.add(dedupKey); continue; }
        }

        // «Critical Lift» — так же засчитывается поданной формой
        if (isCriticalLiftDoc(m.documentName)) {
            const ok = await hasLiftEvaluationSubmittedLocal(jhaData);
            if (ok) { seen.add(dedupKey); continue; }
        }

        seen.add(dedupKey);
        missing.push({
            document_name: m.documentName,
            document_key: m.documentKey,
            reason: `Triggered by — ${describeMatch(m)}`,
        });
    }
    return missing;
}

/**
 * Требуется ли Fall Arrest Rescue Plan для этого JHA — по ПОЛНОМУ оценщику
 * (поддерживает условия по Work Activity / Energy / Hazard, а не только
 * work-activity-only как legacy getTriggerValues). Используется заметным
 * FARP-баннером в Step Activity / панели отправки, чтобы он показывался и для
 * energy/hazard-триггеров.
 *
 * @returns {Promise<{ required: boolean, reason: string }>}
 */
export async function evaluateFarpRequirement(jhaData) {
    try {
        const triggers = await loadTriggers();
        if (!triggers.length) return { required: false, reason: '' };
        const matched = evaluateTriggers(triggers, jhaData?.stepThree || {}, jhaData?.stepOne || {});
        const farp = matched.find(m => m.specialDocumentType === SPECIAL_FARP);
        return { required: !!farp, reason: farp ? describeMatch(farp) : '' };
    } catch (e) {
        console.warn('[evaluator] evaluateFarpRequirement failed', e);
        return { required: false, reason: '' };
    }
}

export { SPECIAL_FARP };
